import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import type { ISchedulerData, ISchedulerTask, SiteId } from '@interfaces/IScheduler';
import { fetchSiteLogistics } from '@apis/logistics';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useSchedulerActions } from '../../hooks/useSchedulerStore';
import { taskWindowLabel } from '../../lib/taskDates';
import { previewConsumption, actualDaysElapsed, plannedDays } from '../../stock/consumptionPreview';
import { DependenciesSection } from './DependenciesSection';
import { ChecklistCard } from './ChecklistCard';
import { TaskCommitBlock } from './TaskCommitBlock';
import { TaskLogSection } from './TaskLogSection';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { TaskStatusPill } from '../TaskStatusPill/TaskStatusPill';
import styles from './TaskPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

// ---------------------------------------------------------------------------
// The Overview tab, read top-down as: the commit block (the progress of a
// promised task, the promise's own rows, what stands between the task and its
// next step), the checklist that defines done, then the plain plan facts
// around it — sub-tasks, dependencies — and finally what has happened to the
// task (the log). The step's
// button is the drawer footer's (`TaskStepActions`), not this tab's.
//
// The task's identity (group, name, plan window) is the drawer HEADER's job;
// this tab used to repeat it.
// ---------------------------------------------------------------------------

interface ITaskPanelOverviewTabProps {
  task: ISchedulerTask;
  data: ISchedulerData;
  siteId: SiteId;
  /** Loaded schedule number — threaded to the progress section for immediate % persistence. */
  scheduleNumber?: string;
  /** Optional content rendered at the top of the tab (e.g. the Activities Risk
   *  Assessment). The Scheduler leaves it undefined. */
  topSlot?: ReactNode;
}

export function TaskPanelOverviewTab({ task, data, siteId, scheduleNumber, topSlot }: ITaskPanelOverviewTabProps) {
  const { t } = useTranslation();
  const { openPanel } = useSchedulerActions();

  // The progress %/area edits are an immediate-write surface, so read the LIVE
  // task from `data` (the draft snapshot taken on open goes stale as areas flip).
  const liveTask = data.tasks.find((tk) => tk.id === task.id) ?? task;
  const hasChildren = data.tasks.some((tk) => tk.parentId === task.id);
  // Three shapes of row, and the drawer says different things about each. A
  // LEAF is promised and reported on in its own right (its facts are the commit
  // block's rows); a SUMMARY rolls its children up, so its plan facts stay in
  // the grid below; a MILESTONE is a date — no promise, no workers, no
  // roll-up, just the block's manual progress and its one big Done.
  const isMilestone = liveTask.isMilestone;
  const isSummary = hasChildren && !isMilestone;

  // Completion preview (spec §7): what completing THIS task right now would
  // actually deduct from stock, on top of what time already consumed. Reads
  // the same shared `['logistics', siteId]` cache entry the Stock tab and the
  // Logistics page use — `enabled` skips the fetch entirely for the common
  // case (no claims, or not yet in-progress) rather than opening a second,
  // redundant fetch path.
  // The site's zone GATES the helper rather than being defaulted (frontend
  // CLAUDE.md §6): every line below turns an instant into the site's calendar
  // day, and `previewConsumption` builds an `Intl.DateTimeFormat` from it —
  // which throws outright on the `''` a default would hand it.
  const tz = useSelector(selectSiteTz);
  const showStockHelper =
    tz !== undefined && (liveTask.stockClaims?.length ?? 0) > 0 && liveTask.committed && liveTask.status !== 'done';
  const logisticsQuery = useQuery({
    queryKey: ['logistics', siteId],
    queryFn: ({ signal }) => fetchSiteLogistics(siteId, signal),
    enabled: showStockHelper,
  });
  // The helper's zone, once everything it needs is in hand — `undefined` while
  // the tz is unresolved OR the levels are still loading. One narrowed value
  // both gates the render and carries the zone into it. `enabled` keeps the
  // query PENDING when there is nothing to preview, so the pending check
  // cannot fold into `showStockHelper` (that would disable the query it waits
  // on) — while it is still in flight, an empty inventory would render the
  // "already covered" line, which is a claim about the site, not about the
  // loading state.
  const helperTz = showStockHelper && !logisticsQuery.isPending ? tz : undefined;
  const todayIso = new Date().toISOString();
  const stockPreview =
    helperTz === undefined
      ? []
      : previewConsumption(liveTask, logisticsQuery.data?.inventory ?? [], todayIso, helperTz);
  const stockDeductItems = stockPreview.filter((item) => item.deduct > 0);

  // Direct children of this task. A task with ≥1 child renders as a summary bar;
  // surfacing them here lets the user drill into the subtree from the drawer.
  const subtasks = useMemo(
    () =>
      data.tasks
        .filter((sub) => sub.parentId === task.id)
        .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0)),
    [data.tasks, task.id],
  );

  return (
    <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
      {/* The promise is the subject of this drawer: how it is going, what it
          holds, and what stands between the task and its next step. */}
      <TaskCommitBlock siteId={siteId} taskId={task.id} scheduleNumber={scheduleNumber} />

      {/* The promise's definition of done, right under the promise: what
          stands between the task and Mark done. */}
      {(liveTask.checklist?.length ?? 0) > 0 && (
        <ChecklistCard taskId={task.id} siteId={siteId} scheduleNumber={scheduleNumber} tz={tz} />
      )}

      {topSlot}

      {/* What completing NOW would deduct from stock. */}
      {helperTz !== undefined && (
        <section className={styles.overviewActions}>
          {stockDeductItems.length > 0 ? (
            stockDeductItems.map((item) => (
              <p key={item.materialName} className={styles.actionHelper}>
                {t('scheduler.panel.actions.stockDeduct', {
                  day: actualDaysElapsed(liveTask, todayIso, helperTz),
                  planned: plannedDays(liveTask),
                  qty: item.deduct,
                  unit: item.unit,
                  material: item.materialName,
                  byTime: item.consumedByTime,
                })}
              </p>
            ))
          ) : (
            <p className={styles.actionHelper}>{t('scheduler.panel.actions.stockCovered')}</p>
          )}
        </section>
      )}

      {/* What is NOT part of the promise. A leaf's profession, assignee and
          safety flag are rows of the commit block above; a summary has no
          promise of its own, so they stay here as plain facts. */}
      {!isMilestone && (
        <section className={styles.readonlyGrid}>
          <Field label={t('scheduler.panel.fields.workers')}  value={task.workerCount?.toString() ?? '—'} />
          {isSummary && task.profession && (
            <div className={styles.field}>
              <label>{t('scheduler.panel.fields.profession')}</label>
              <div className={styles.readonlyValue}>
                <ProfessionPill profession={task.profession} showContractor />
              </div>
            </div>
          )}
          {isSummary && (
            <Field label={t('scheduler.panel.fields.progress')} value={`${liveTask.progressPercent}%`} />
          )}
        </section>
      )}

      {task.notes && (
        <section className={styles.field}>
          <label>{t('scheduler.panel.fields.notes')}</label>
          <p className={styles.readonlyNotes}>{task.notes}</p>
        </section>
      )}

      {subtasks.length > 0 && (
        <section className={styles.subtaskSection}>
          <h3 className={styles.subtaskTitle}>{t('scheduler.panel.subtasks', { count: subtasks.length })}</h3>
          <ul className={styles.subtaskList}>
            {subtasks.map((sub) => (
              <li key={sub.id}>
                <button
                  type="button"
                  className={styles.subtaskRow}
                  onClick={() => openPanel({ mode: 'edit', taskId: sub.id, initialTab: 'overview' })}
                >
                  <span className={styles.subtaskName}>{sub.name}</span>
                  <span className={styles.subtaskMeta}>
                    <TaskStatusPill status={sub.status} />
                    <span className={styles.subtaskDates}>{taskWindowLabel(sub, 'MM/dd')}</span>
                    <span className={styles.subtaskProgress}>{sub.progressPercent}%</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <DependenciesSection task={task} mode="edit" data={data} readOnly />


      <TaskLogSection entries={liveTask.log ?? []} tz={tz} />
    </ScrollArea>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <p className={styles.readonlyValue}>{value}</p>
    </div>
  );
}
